import { cx } from './cx.js';
import { SuitGlyph, type SuitName } from './SuitLine.js';

/**
 * The motif at the size of a room: two paper suits on the ground.
 *
 * Mitch chose this on 2026-09-07 over the alpha scatter, the animated fields
 * and the flat cut (BRAND § The ground motif). A white spade bleeds off the
 * top-left corner and a tangerine-quiet heart off the bottom-right, each cut
 * from paper: an opaque face, no rim, and a 3px hard offset in gray-300 with
 * no blur. The middle stays clear because the controls stand there, and the
 * controls keep their ink shadows, so the ground stays quieter than the
 * actions. Nothing here is a new mark or a new color: two of the settled four
 * drawings, in stops the palette already has.
 *
 * Two compositions, one per device, because a phone and a laptop are not the
 * same picture scaled: the phone's heart sits in the open band above the
 * rooms and further off the right edge so its contour does not cut through a
 * chip. Positions are a percentage of the surface, sizes are points. Left to
 * itself the ground reads the `tablet` breakpoint; the sketchbook, which
 * stands a phone frame inside a laptop-wide page, says which it is with
 * `compact`.
 *
 * Decorative and inert: `aria-hidden` and `pointer-events-none`, so it neither
 * reaches a screen reader nor stands between a thumb and a chip.
 */

/** A glyph on the ground: position as a percentage of the surface, size in points. */
export interface QuietPlacement {
  readonly suit: SuitName;
  readonly left: number;
  readonly top: number;
  readonly size: number;
  readonly tilt: number;
}

export const QUIET_PHONE: readonly QuietPlacement[] = [
  { suit: 'spade', left: -24, top: -12, size: 320, tilt: -18 },
  { suit: 'heart', left: 66, top: 66, size: 190, tilt: 14 },
];

export const QUIET_LAPTOP: readonly QuietPlacement[] = [
  { suit: 'spade', left: -13, top: -27, size: 540, tilt: -18 },
  { suit: 'heart', left: 74, top: 62, size: 460, tilt: 14 },
];

/* The paper face: white for the ink suits, the resting stop for the colored ones. */
const FACE = {
  spade: 'text-ground-light',
  heart: 'text-tangerine-quiet',
  diamond: 'text-teal-quiet',
  club: 'text-ground-light',
} satisfies Record<SuitName, string>;

/* The hard offset, in points. The shadow is a second cut of the same glyph. */
const OFFSET = 3;

const GROUND = 'pointer-events-none absolute inset-0 size-full overflow-hidden';

function Field({
  glyphs,
  className,
}: {
  readonly glyphs: readonly QuietPlacement[];
  readonly className?: string | undefined;
}): React.JSX.Element {
  return (
    <svg aria-hidden="true" className={cx(GROUND, className)}>
      {glyphs.map((glyph, index) => (
        <svg
          key={index}
          x={`${glyph.left}%`}
          y={`${glyph.top}%`}
          width={glyph.size}
          height={glyph.size}
          overflow="visible"
        >
          <g transform={`rotate(${glyph.tilt} ${glyph.size / 2} ${glyph.size / 2})`}>
            <svg
              x={OFFSET}
              y={OFFSET}
              width={glyph.size}
              height={glyph.size}
              className="text-gray-300"
            >
              <SuitGlyph suit={glyph.suit} className="size-full" />
            </svg>
            <svg width={glyph.size} height={glyph.size} className={FACE[glyph.suit]}>
              <SuitGlyph suit={glyph.suit} className="size-full" />
            </svg>
          </g>
        </svg>
      ))}
    </svg>
  );
}

export interface QuietGroundProps {
  /**
   * Which composition to draw. Leave it out and the ground reads the `tablet`
   * breakpoint; a frame that knows its own device says so.
   */
  readonly compact?: boolean | undefined;
  /** Placement and stacking. The glyphs, their sizes and their colors are not yours. */
  readonly className?: string | undefined;
}

export function QuietGround({ compact, className }: QuietGroundProps): React.JSX.Element {
  if (compact !== undefined) {
    return <Field glyphs={compact ? QUIET_PHONE : QUIET_LAPTOP} className={className} />;
  }
  return (
    <>
      <Field glyphs={QUIET_PHONE} className={cx('tablet:hidden', className)} />
      <Field glyphs={QUIET_LAPTOP} className={cx('hidden tablet:block', className)} />
    </>
  );
}
